import { type Failure, RequestFailed, malformed, readJson, request } from './request'

/**
 * The timetable, as the service describes it.
 *
 * **The service never sends a formatted time.** `startsAt` is an ISO moment and `zone` is the zone
 * the appointment was spoken in; the local time is worked out here with `Intl`. That split is the whole
 * reason a week grid can be right: a moment alone would slide the 8:00 lecture off Tuesday morning
 * for a reader standing somewhere else, and a local time alone could not answer "what is on now".
 *
 * `held` is the other field worth knowing about. It names what somebody corrected by hand, and what
 * a source will therefore never overwrite again — which is a promise, so it has to be visible and
 * has to be takeable back.
 */

export interface Appointment {
  readonly id: string
  readonly title: string
  /** When it begins, as an ISO moment. */
  readonly startsAt: string
  readonly endsAt: string
  /** The zone it was spoken in — an IANA name like `Europe/Berlin`. */
  readonly zone: string
  readonly allDay: boolean
  readonly room: string
  readonly teacher: string
  readonly note: string
  /** The markup the note is written in, as a media type. */
  readonly noteMarkup: string
  readonly cancelled: boolean
  /** What the source says about a deviation: a substitution, a move, a room change. */
  readonly remark: string
  readonly seriesId: string | null
  /** Exactly one subject or none — never a list. */
  readonly subject: AppointmentSubject | null
  /** What the source calls the subject. Shown as text where there is no MyLib subject yet. */
  readonly sourceSubject: SourceSubject
  readonly origin: AppointmentOrigin
  /** `title`, `time`, `room`, `teacher`, `subject` — what a source no longer overwrites. */
  readonly held: readonly string[]
  /** When the source stopped offering it, or `null`. Marked, never deleted. */
  readonly goneFromSourceAt: string | null
  /**
   * The first line of what was written about this session, or `null` where nothing was.
   *
   * The first line and not the text: a day shows six appointments, and one long write-up must not
   * push the rest of the day off the screen. It travels with the appointment so that a day is one
   * request and not seven.
   */
  readonly writeupLine: string | null
  /** How many pieces of content are linked to this appointment. */
  readonly links: number
  readonly createdAt: string
  readonly updatedAt: string
}

/**
 * What somebody wrote about a session afterwards.
 *
 * It is **not** {@link Appointment.note}, and the difference is when the two are written: the note is
 * the announcement before the session — bring the script, the room has moved — and this is what came
 * of it.
 */
export interface Writeup {
  readonly text: string
  /** Which markup it is written in, as a media type. */
  readonly format: string
  readonly createdAt: string
  readonly updatedAt: string
}

export interface AppointmentSubject {
  readonly id: string
  readonly name: string
  /**
   * Where new things at this subject are filed, or `null`.
   *
   * It travels with the appointment for one act: uploading a document while writing about a session.
   * Asking which folder at that moment would be the form this surface exists not to be.
   */
  readonly rootFolderId: string | null
}

export interface SourceSubject {
  readonly key: string
  readonly name: string
}

export interface AppointmentOrigin {
  /** `by-hand`, `calendar-file` or `connected-system`. */
  readonly kind: string
  readonly source: string
  readonly seenAt: string | null
  /** Whether it can be recognised again on a second reading. `false` means a second copy. */
  readonly canBeRecognised: boolean
}

export interface Series {
  readonly id: string
  readonly title: string
  readonly everyWeeks: number
  /** `MONDAY`, `TUESDAY`, … in the order a week has them. */
  readonly weekdays: readonly string[]
  readonly startsAt: string
  readonly endsAt: string
  readonly zone: string
  readonly from: string
  readonly until: string
  readonly createdAt: string
  readonly updatedAt: string
}

export interface CreatedSeries {
  readonly series: Series
  readonly appointments: readonly Appointment[]
}

/** A calendar this space subscribes to, and how the last reading of it went. */
export interface Calendar {
  readonly id: string
  readonly name: string
  readonly url: string
  readonly lastReadAt: string | null
  readonly lastResult: string
  /** Why the last reading failed, or empty. A subscription that fails quietly is the worst kind. */
  readonly lastError: string
  readonly createdAt: string
  readonly updatedAt: string
}

/** What one reading of a calendar did. */
export interface TakeOver {
  readonly added: readonly TakenOver[]
  readonly changed: readonly TakenOver[]
  readonly untouched: readonly TakenOver[]
  readonly missing: readonly TakenOver[]
  /** What the reading could not do, in words: an unknown repetition, an unknown zone, no ids. */
  readonly notes: readonly string[]
  readonly total: number
}

export interface TakenOver {
  readonly appointmentId: string | null
  readonly title: string
  readonly startsAt: string
  readonly zone: string
  readonly room: string
  readonly why: string
}

export interface WantedAppointment {
  readonly title?: string
  readonly startsAt?: string
  readonly endsAt?: string
  readonly zone?: string
  readonly allDay?: boolean
  readonly room?: string
  readonly teacher?: string
  readonly note?: string
  readonly noteMarkup?: string
  readonly subjectId?: string | null
  readonly clearSubject?: boolean
}

export interface WantedSeries {
  readonly title: string
  readonly everyWeeks: number
  readonly weekdays: readonly string[]
  readonly startsAt: string
  readonly endsAt: string
  readonly zone?: string
  readonly from: string
  readonly until: string
  readonly room?: string
  readonly teacher?: string
  readonly note?: string
  readonly noteMarkup?: string
  readonly subjectId?: string | null
}

/** What an appointment is about, in words: its subject, or failing that whatever the source calls it. */
export function whatItIsAbout(appointment: Appointment): string {
  if (appointment.subject !== null && appointment.subject.name !== '') {
    return appointment.subject.name
  }
  return appointment.sourceSubject.name !== ''
    ? appointment.sourceSubject.name
    : appointment.sourceSubject.key
}

const TIMETABLE = '/api/timetable'

export async function fetchAppointments(
  from: Date,
  until: Date,
  subjectId?: string | null,
  signal?: AbortSignal,
): Promise<Appointment[]> {
  const where = new URLSearchParams({ from: from.toISOString(), until: until.toISOString() })
  if (subjectId !== undefined && subjectId !== null && subjectId !== '') {
    where.set('subject', subjectId)
  }
  const response = await request(`${TIMETABLE}?${where.toString()}`, { signal })
  const payload: unknown = await response.json().catch(() => null)
  if (!Array.isArray(payload)) {
    throw malformed('The answer did not describe a list of appointments.')
  }
  return payload as Appointment[]
}

/**
 * One appointment, by its id alone.
 *
 * The one request that does not start from a week. Everything else here asks "what is on between
 * these two moments"; this asks "when is this on", and it is what lets somebody arrive from a link or
 * from a subject — both of which know a kind and an id and deliberately nothing else — and still land
 * on the week the appointment is actually in.
 */
export async function fetchAppointment(id: string, signal?: AbortSignal): Promise<Appointment> {
  const response = await request(`${TIMETABLE}/${encodeURIComponent(id)}`, { signal })
  const payload: unknown = await response.json().catch(() => null)
  if (payload === null || typeof payload !== 'object') {
    throw malformed('The answer did not describe an appointment.')
  }
  return payload as Appointment
}

export async function fetchSeries(signal?: AbortSignal): Promise<Series[]> {
  const response = await request(`${TIMETABLE}/series`, { signal })
  const payload: unknown = await response.json().catch(() => null)
  if (!Array.isArray(payload)) {
    throw malformed('The answer did not describe a list of series.')
  }
  return payload as Series[]
}

export async function addAppointment(wanted: WantedAppointment): Promise<Appointment> {
  return (await change(TIMETABLE, 'POST', wanted)) as Appointment
}

/**
 * A correction — and every field it names is held from then on.
 *
 * `scope` says whether it is this appointment alone or every appointment of its series. It is asked rather than
 * guessed, because there is no honest default.
 */
export async function changeAppointment(
  id: string,
  wanted: WantedAppointment,
  scope: 'one' | 'series' = 'one',
): Promise<Appointment> {
  return (await change(`${TIMETABLE}/${encodeURIComponent(id)}`, 'PUT', {
    ...wanted,
    scope,
  })) as Appointment
}

/** Takes one hand correction back, so the source's own value applies again at the next reading. */
export async function releaseHold(id: string, fact: string): Promise<Appointment> {
  return (await change(
    `${TIMETABLE}/${encodeURIComponent(id)}/held/${encodeURIComponent(fact)}`,
    'DELETE',
  )) as Appointment
}

export async function removeAppointment(id: string): Promise<void> {
  await change(`${TIMETABLE}/${encodeURIComponent(id)}`, 'DELETE')
}

/**
 * Makes this appointment the first of a run.
 *
 * On the appointment, because everything a repetition needs but "how often" and "until when" is
 * already standing on it — the day, the two times, the room, the subject.
 */
export async function repeatAppointment(
  id: string,
  everyWeeks: number,
  weekdays: readonly string[],
  until: string,
): Promise<CreatedSeries> {
  return (await change(`${TIMETABLE}/${encodeURIComponent(id)}/repeat`, 'POST', {
    everyWeeks,
    weekdays,
    until,
  })) as CreatedSeries
}

/** Stops the repetition, and keeps every appointment it made. */
export async function stopRepeating(id: string): Promise<void> {
  await change(`${TIMETABLE}/${encodeURIComponent(id)}/repeat`, 'DELETE')
}

export async function addSeries(wanted: WantedSeries): Promise<CreatedSeries> {
  return (await change(`${TIMETABLE}/series`, 'POST', wanted)) as CreatedSeries
}

/** Removes a series, and its appointments only if that is asked for. Keeping them is the default. */
export async function removeSeries(id: string, alsoAppointments: boolean): Promise<void> {
  const what = alsoAppointments ? 'remove' : 'keep'
  await change(`${TIMETABLE}/series/${encodeURIComponent(id)}?appointments=${what}`, 'DELETE')
}

/**
 * What was written about this session, or `null` where nothing was.
 *
 * `null` is the ordinary answer — most sessions are never written about — and it is an answer rather
 * than an absence, so a screen never has to tell "nothing there" from "did not load".
 */
export async function fetchWriteup(id: string, signal?: AbortSignal): Promise<Writeup | null> {
  const response = await request(`${TIMETABLE}/${encodeURIComponent(id)}/writeup`, { signal })
  const payload = await readJson<{ writeup: Writeup | null }>(response)
  return payload.writeup
}

/**
 * Writes down what came of a session — and **empties it by writing nothing**.
 *
 * There is no separate call for deleting one, because there is no such thing as an empty write-up:
 * emptying it and deleting it are one gesture. The appointment and everything linked to it are
 * untouched either way, which is why nothing asks first.
 */
export async function writeAboutIt(
  id: string,
  text: string,
  format?: string,
): Promise<Writeup | null> {
  const answered = (await change(`${TIMETABLE}/${encodeURIComponent(id)}/writeup`, 'PUT', {
    text,
    ...(format === undefined ? {} : { format }),
  })) as { writeup: Writeup | null }
  return answered.writeup
}

export async function fetchCalendars(signal?: AbortSignal): Promise<Calendar[]> {
  const response = await request(`${TIMETABLE}/calendars`, { signal })
  const payload: unknown = await response.json().catch(() => null)
  if (!Array.isArray(payload)) {
    throw malformed('The answer did not describe a list of calendars.')
  }
  return payload as Calendar[]
}

/**
 * Subscribes to a calendar, and reads it once straight away.
 *
 * The immediate reading is the point: an address that answers with something that is not a calendar
 * is refused before anything is kept, so nobody is left with a subscription that never brings
 * anything and never says why.
 */
export async function subscribeToCalendar(name: string, url: string): Promise<Calendar> {
  return (await change(`${TIMETABLE}/calendars`, 'POST', { name, url })) as Calendar
}

export async function changeCalendar(id: string, name: string, url: string): Promise<Calendar> {
  return (await change(`${TIMETABLE}/calendars/${encodeURIComponent(id)}`, 'PUT', {
    name,
    url,
  })) as Calendar
}

/** Reads one now, rather than waiting for the run. */
export async function readCalendar(id: string): Promise<TakeOver> {
  return (await change(`${TIMETABLE}/calendars/${encodeURIComponent(id)}/read`, 'POST')) as TakeOver
}

/** Stops subscribing. Every appointment it brought stays exactly where it is. */
export async function unsubscribeFromCalendar(id: string): Promise<void> {
  await change(`${TIMETABLE}/calendars/${encodeURIComponent(id)}`, 'DELETE')
}

/** The statuses that carry a code this side knows how to say out loud. */
const REFUSALS = [400, 404, 409, 413, 415, 502] as const

const SENTENCES: Readonly<Record<string, string>> = {
  'no-such-appointment': 'That appointment is not there any more.',
  'no-such-series': 'That series is not there any more.',
  'empty-appointment': 'An appointment has to say what it is.',
  'appointment-ends-before-it-starts': 'An appointment cannot end before it starts.',
  'not-a-series': 'That is not a repetition MyLib can make sense of.',
  'unknown-zone': 'That is not a time zone MyLib knows. Use a name like Europe/Berlin.',
  'not-a-time': 'That is not a time MyLib can read.',
  'no-such-calendar': 'That calendar is not there any more.',
  'not-a-calendar':
    'What is at that address is not a calendar. MyLib reads an ICS feed — the one ILIAS hands out under /calendar.php.',
  'not-a-calendar-address': 'That is not an address MyLib can subscribe to.',
  'calendar-unreachable': 'MyLib could not read that address.',
  'calendar-could-not-be-read':
    'MyLib fell over what that calendar answered with. What went wrong stands at the calendar.',
  'calendar-already-subscribed': 'This space already subscribes to that calendar.',
  'appointment-too-long': 'That is longer than an appointment holds.',
  'writeup-too-long':
    'That is longer than a write-up holds. Something this long wants to be a document.',
}

async function change(address: string, method: string, body?: unknown): Promise<unknown> {
  const response = await request(address, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    expect: [...REFUSALS],
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  if (!response.ok) {
    throw refusalIn(await response.text(), response.status)
  }

  return response.status === 204 ? null : readJson<unknown>(response)
}

function parse(body: string): { code?: string; message?: string } | null {
  try {
    return JSON.parse(body) as { code?: string; message?: string }
  } catch {
    return null
  }
}

function refusalIn(body: string, status: number): RequestFailed {
  const problem = parse(body)
  const known = problem?.code === undefined ? undefined : SENTENCES[problem.code]

  const failure: Failure = {
    kind: status >= 500 ? 'service' : 'refused',
    message: known ?? problem?.message ?? 'MyLib could not do that.',
    detail: known === undefined ? null : (problem?.message ?? null),
    retryable: status >= 500,
    code: problem?.code ?? null,
  }
  return new RequestFailed(failure)
}
